import { useEffect, useState } from 'react'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import MainTitle from '../../components/MainTitle'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUserSlash,
  faCalendarDay,
  faUsers,
  faNotesMedical,
  faCalendarCheck,
  faUserMd,
  faClock,
  faUserGroup,
  faChevronDown, 
  faChevronUp,
  faSpinner, 
} from '@fortawesome/free-solid-svg-icons'
import FigureCard from '../../components/FigureCard'


function AdminDashboard() {
  const axios = useAxiosPrivate()

  const [counsellors, setCounsellors] = useState([])
  const [availableSlots, setAvailableSlots] = useState([])
  const [availableCounsellors, setAvailableCounsellors] = useState([])
  const [activeTab, setActiveTab] = useState('availability')
  const [usageStats, setUsageStats] = useState({})
  const [todaysStats, setTodaysStats] = useState({})
  const [expanded, setExpanded] = useState({})
  const [expandedService, setExpandedService] = useState({})
  // const [clinicalRequests, setClinicalRequests] = useState([])
  const [workingCounsellors, setWorkingCounsellors] = useState([])
  const [sessionsByService, setSessionsByService] = useState([])
  const [selectedRange, setSelectedRange] = useState('today')
  const [selectedRangeTitle, setSelectedRangeTitle] = useState('Today')
  const [clinicalRequestsCounts, setClinicalRequestsCounts] = useState({
    'Clinical Request': 0,
    'Extension Requests': 0,
  })
  const [statsLoading, setStatsloading] = useState(false)

  useEffect(() => {
    setStatsloading(false)
  }, [usageStats])

  useEffect(() => {
    fetchAllStaticData()
    fetchAllLiveData()

    const interval = setInterval(() => {
      fetchAllLiveData()
    }, 300000)

    return () => clearInterval(interval)
  }, [])

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const toggleService = (service) => {
    setExpandedService((prev) => ({
      ...prev,
      [service]: !prev[service]
    }))
  }


  const fetchAllStaticData = async () => {
    try {
      const [counsellorRes, serviceRes] = await Promise.all([
        fetchCounsellors(),
        fetchSessionsByService(),
      ])
      setCounsellors(counsellorRes)
      setSessionsByService(serviceRes)
      fetchClinicalRequestCounts()
  
    } catch (err) {
      console.error('Static dashboard data error:', err)
    }
  }

  const fetchAllLiveData = async () => {
    try {
      setStatsloading(true)
      const [
        slotsRes,
        counsellorRes,
        usageRes,
        // clinicalRes,
        figuresRes,
        workingRes,
      ] = await Promise.all([
        fetchAvailableSlots(),
        fetchAvailableCounsellors(),
        fetchUsageStats(),
        // fetchClinicalRequests(),
        fetchFigures(),
        fetchWorkingCounsellors(),
      ])
  
      setAvailableSlots(slotsRes)
      setAvailableCounsellors(counsellorRes)
      // setClinicalRequests(clinicalRes)
      setWorkingCounsellors(workingRes)
  
      setUsageStats({
        sessionsBooked: usageRes.sessionsBooked,
        counsellorsWorking: figuresRes.counsellorsWorking,
        clientsSeenToday: figuresRes.clientsSeen,
        clientsSeen: usageRes.clientsSeen,
        assessmentCount: usageRes.assessmentCount,
        clinicianCancelledSessions: usageRes.clinicianCancelledSessions,
        clientCancelledSessions: usageRes.clientCancelledSessions,
        // completedSessions: usageRes.completedSessions,
        confirmedSessions: usageRes.confirmedSessions,
        therapyCount: usageRes.therapyCount
      })

      setTodaysStats({
        sessionsBooked: figuresRes.sessionsBooked,
        upcomingSessions: figuresRes.upcomingSessions,
        cancelledSessions: figuresRes.cancelledSessions,
        completedSessions: figuresRes.completedSessions,
      })
    } catch (err) {
      console.error('Live dashboard data error:', err)
    }
  }

  const handleDateRangeChange = async (e) => {
    const newRange = e.target.value
    setSelectedRange(newRange)
    setSelectedRangeTitle(newRange.replace(/_/g, ' ').replace(/(^\w|\s\w)/g, m => m.toUpperCase()))
    const newStats = await fetchUsageStats(newRange)
    setUsageStats(prev => ({
      ...prev,
      ...newStats
    }))
  }
  
  const fetchCounsellors = async () => {
    const res = await axios.get('/admin/counsellors/no-future-sessions')
    return res.data.result
  }

  const fetchAvailableSlots = async () => {
    const res = await axios.get('/admin/sessions/available-today')
    return res.data.result
  }

  const fetchAvailableCounsellors = async () => {
    const res = await axios.get('/admin/sessions/available-counsellors-today')
    return res.data.result
  }

  const fetchUsageStats = async (range = 'today') => {
    setStatsloading(true)
    const res = await axios.get(`/admin/stats/usage?range=${range}`)
    return res.data.result
  }

  // const fetchClinicalRequests = async () => {
  //   const res = await axios.get('/admin/clinical-requests')
  //   return res.data.result
  // }

  const fetchClinicalRequestCounts = async () => {
    try {
      const res = await axios.get('/admin/clinical-requests')
      const result = res.data.result          
      setClinicalRequestsCounts(result)
    } catch (err) {
      console.error('Error fetching clinical request counts:', err)
    }
  }

  const fetchFigures = async () => {
    const res = await axios.get('/admin/figures/today')
    return res.data.result
  }

  const fetchWorkingCounsellors = async () => {
    const res = await axios.get('/admin/working-counsellors')
    return res.data.result
  }

  const fetchSessionsByService = async () => {
    const res = await axios.get('/admin/sessions-by-service-today')
    return res.data.result
  }

  return (
    <section>
      <MainTitle title="Admin Dashboard" />

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <FigureCard
          title="Total Sessions for Today"
          value={todaysStats.sessionsBooked || 0}
          color="blue"
          icon={faCalendarCheck}
        />
        <FigureCard
          title="Upcoming Sessions"
          value={todaysStats.upcomingSessions || 0}
          color="green"
          icon={faUserMd}
        />
        <FigureCard
          title="Cancelled Sessions"
          value={todaysStats.cancelledSessions || 0}
          color="yellow"
          icon={faClock}
        />
        <FigureCard
          title="Completed Sessions"
          value={todaysStats.completedSessions || 0}
          color="purple"
          icon={faUserGroup}
        />
      </section>
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Figures */}
        <article className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200">
          <div className="flex items-center mb-4 text-blue-600 justify-between">
            <div className='flex items-center'>
              <FontAwesomeIcon icon={faUsers} className="w-5 h-5 mr-2" />
              <h3 className="text-lg font-semibold">{selectedRangeTitle}'s Stats</h3>
              {statsLoading && 
                <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 mr-2 animate-spin ml-4" />
              }
            </div>
            <select onChange={handleDateRangeChange} className="text-sm border rounded px-2">
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last_week">Last Week</option>
              <option value="last_month">Last Month</option>
            </select>
          </div>
          <div className="space-y-2 text-sm text-gray-700">
            <p><strong>Sessions Created:</strong> {usageStats.sessionsBooked || 0}</p>
            <p><strong>Sessions Cancelled (Clinician):</strong> {usageStats.clinicianCancelledSessions || 0}</p>
            <p><strong>Sessions Cancelled (Client):</strong> {usageStats.clientCancelledSessions || 0}</p>
            <p><strong>Confirmed Sessions:</strong> {usageStats.confirmedSessions || 0}</p>
            <p><strong>Number of Clients Seen:</strong> {usageStats.clientsSeen || 0}</p>
          </div>
        </article>

        {/* Counsellors Without Future Sessions */}
        <article className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200">
          <div className="flex items-center mb-4 text-red-600">
            <FontAwesomeIcon icon={faUserSlash} className="w-5 h-5 mr-2" />
            <h3 className="text-lg font-semibold">Counsellors Without Future Sessions</h3>
          </div>
          {Object.values(counsellors).length > 0 ? (
            <ul className="text-sm text-gray-700 space-y-2 max-h-48 overflow-y-auto pr-2">
              {Object.values(counsellors).map(c => (
                <li key={c.id} className="border-b pb-2">
                  <span className="font-medium">{c.name}</span>
                  <span className="text-gray-500 text-xs ml-2">
                    Last session:{' '}
                    {c.lastSession ? moment(c.lastSession).format('DD MMM YYYY') : 'None'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">All counsellors have upcoming sessions.</p>
          )}
        </article>

        
        {/* Available Time Slots Today */}
        <article className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200">
          <div className="flex items-center mb-4 text-green-600">
            <FontAwesomeIcon icon={faCalendarDay} className="w-5 h-5 mr-2" />
            <h3 className="text-lg font-semibold">Available Time Slots (Today)</h3>
          </div>
          <div className="flex space-x-4 mb-4 border-b">
            <button
              onClick={() => setActiveTab('availability')}
              className={`text-sm font-semibold ${
                activeTab === 'availability' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'
              }`}
            >
              Availability
            </button>
            <button
              onClick={() => setActiveTab('timeSlots')}
              className={`text-sm font-semibold ${
                activeTab === 'timeSlots' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'
              }`}
            >
              Time Slots
            </button>
          </div>
          {activeTab === 'availability' ? (
            Object.keys(availableCounsellors).length > 0 ? (
              <div className="text-sm text-gray-700 space-y-4 max-h-64 overflow-y-auto pr-2">
                {Object.entries(availableCounsellors).map(([service, slots]) => (
                  <div key={service}>
                    <div
                      className="flex justify-between items-center cursor-pointer text-sm font-semibold text-gray-800 mb-1"
                      onClick={() => toggleService(service)}
                    >
                      <div className="space-x-1">
                        <span>{service}:</span>
                        <span>{slots.length}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        <FontAwesomeIcon icon={expandedService[service] ? faChevronUp : faChevronDown} />
                      </span>
                    </div>
                    {expandedService[service] && (
                      <ul className="space-y-1">
                        {slots.map((slot, index) => (
                          <li key={index} className="flex justify-between items-center border-b pb-1">
                            <span className="font-medium">{slot.counsellor}</span>
                            <span className="text-gray-500">
                              {moment(slot.start).format('HH:mm')} - {moment(slot.end).format('HH:mm')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No available slots today.</p>
            )
          ) : (
            <div className="text-sm text-gray-700 space-y-1 max-h-64 overflow-y-auto pr-2">
              {Object.entries(availableSlots).map(([timeRange, count]) => (
                <div key={timeRange} className="flex justify-between border-b py-1">
                  <span className="font-medium">{timeRange}</span>
                  <span className="text-gray-500">
                    {count} counsellor{count > 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </article>

        {/* Clinical Requests and Session Extensions */}
        <article className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200">
          <div className="flex items-center mb-4 text-indigo-600">
            <FontAwesomeIcon icon={faNotesMedical} className="w-5 h-5 mr-2" />
            <h3 className="text-lg font-semibold">Notifications Done Today</h3>
          </div>

          <ul className="text-sm text-gray-700 space-y-2">
            <li>
              <span className="font-semibold pl-2">Admin Requests:</span>{' '}
              {clinicalRequestsCounts?.['Refer to Admin'] ?? 0}
            </li>
            <li>
              <span className="font-semibold pl-2">Clinical Requests:</span>{' '}
              {clinicalRequestsCounts?.['Refer to Clinic'] ?? 0}
            </li>
            <li>
              <span className="font-semibold pl-2">Extension Requests:</span>{' '}
              {clinicalRequestsCounts?.['Session Extension Request'] ?? 0}
            </li>
            <li>
              <span className="font-semibold pl-2">No Future Sessions:</span>{' '}
              {clinicalRequestsCounts?.['No Future Sessions'] ?? 0}
            </li>
            <li>
              <span className="font-semibold pl-2">Request to Discharge:</span>{' '}
              {clinicalRequestsCounts?.['Request to Discharge'] ?? 0}
            </li>
            <li>
              <span className="font-semibold pl-2">Waiting List:</span>{' '}
              {clinicalRequestsCounts?.['Waiting List'] ?? 0}
            </li>
          </ul>
        </article>

        {/* Counsellors working hours */}
        <article className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200">
          <div className="flex items-center mb-4 text-pink-600">
            <FontAwesomeIcon icon={faUsers} className="w-5 h-5 mr-2" />
            <h3 className="text-lg font-semibold">Counsellors Sessions (Today)</h3>
          </div>

          {workingCounsellors.length > 0 ? (
            <ul className="text-sm text-gray-700 space-y-4 max-h-64 overflow-y-auto pr-2">
              {workingCounsellors.map(c => (
                <li key={c.id} className="border-b pb-2">
                  <button
                    onClick={() => toggleExpand(c.id)}
                    className="w-full text-left font-medium mb-1 flex justify-between items-center"
                  >
                    <span>{c.name}:  {c.sessions.length}</span>
                    <FontAwesomeIcon icon={expanded[c.id] ? faChevronUp : faChevronDown} />
                  </button>

                  {expanded[c.id] && (
                    <ul className="pl-4 space-y-1 text-xs text-gray-600">
                      {c.sessions.map(s => (
                        <li className="flex justify-between items-center" key={s.id}>
                          <span>{s.client}</span>
                          <span>{moment(s.start_time).format('HH:mm')} - {moment(s.end_time).format('HH:mm')}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No counsellors are working today.</p>
          )}
        </article>
        <article className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200">
          <div className="flex items-center mb-4 text-amber-600">
            <FontAwesomeIcon icon={faUserMd} className="w-5 h-5 mr-2" />
            <h3 className="text-lg font-semibold">Sessions by Service (Today)</h3>
          </div>
          {sessionsByService.length > 0 ? (
            <ul className="text-sm text-gray-700 space-y-2 max-h-64 overflow-y-auto pr-2">
              {sessionsByService.map(service => (
                <li key={service.service} className="flex justify-between border-b pb-1">
                  <span className="font-medium">{service.service}</span>
                  <span className="text-gray-600">{service.total} session{service.total !== 1 ? 's' : ''}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No sessions recorded for today.</p>
          )}
        </article>

      </section>
    </section>
  )
}

export default AdminDashboard

